/**
 * iter211g — RectificationGroupCard
 *
 * Renders a diagnostic + rectification pair as a SINGLE grouped card on
 * the customer portal, reading like an invoice:
 *
 *   DIAGNOSTIC INVESTIGATION → BATTERY REPLACEMENT (AGM)
 *   • Diagnostic assessment (1.0h)          £120.00
 *   • Battery (Bosch S5 AGM) + fit + coding £215.00
 *   Group total:                             £335.00
 *
 * The customer approves the WHOLE group in one tap — the diag and the
 * rectification are inseparable (you don't approve the diag charge
 * without the fix, and vice versa).
 *
 * For rectifications with variants (battery Std/AGM/OEM, caliper
 * front/rear/EPB etc.) the card renders a pill selector. Switching
 * calls the portal's variant-switch endpoint which rebuilds parts +
 * labour + charges on the backend and updates the total in place.
 */
import React, { useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight, Check, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const RectificationGroupCard = ({
  token,
  diagJG,
  rectJG,
  approvedIds,
  onApprove,
  onSkip,
  onVariantChanged,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [swapping, setSwapping] = useState(false);

  const rectVariants = rectJG.variants || [];
  // Read the current variant from either the JG payload or the code suffix.
  const activeVariant = rectJG.rectification_variant;

  const groupTotal = (diagJG?.total_inc_vat || diagJG?.subtotal || 0)
                   + (rectJG?.total_inc_vat || rectJG?.subtotal || 0);

  const isApproved = approvedIds?.has?.(rectJG.id);

  const handleSwitchVariant = async (variantCode) => {
    if (variantCode === activeVariant || swapping) return;
    setSwapping(true);
    try {
      const r = await axios.post(
        `${API}/portal/${token}/rectification/${rectJG.id}/variant`,
        { variant_code: variantCode }
      );
      toast.success("Option updated");
      if (onVariantChanged) onVariantChanged(r.data.job_group);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Could not switch option";
      toast.error(msg);
    } finally {
      setSwapping(false);
    }
  };

  return (
    <div
      className={`rounded-xl border-2 overflow-hidden transition-all ${
        isApproved ? "border-emerald-400 bg-emerald-50" : "border-amber-300 bg-white"
      }`}
      data-testid={`rectification-group-card-${rectJG.id}`}
    >
      {/* Header — click to expand/collapse */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600">
            Diagnostic → Rectification
          </p>
          <p className="text-sm font-semibold text-slate-900 mt-0.5 truncate">
            {rectJG.title}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`font-bold text-base ${
            isApproved ? "text-emerald-700" : "text-slate-900"
          }`}>
            £{groupTotal.toFixed(2)}
          </span>
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-500" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100">
          {/* Invoice-style breakdown */}
          <div className="pt-3 space-y-1.5 text-sm">
            {diagJG && (
              <div className="flex items-baseline justify-between text-slate-600">
                <span className="flex-1 min-w-0 pr-3 truncate">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 block">Investigation</span>
                  {diagJG.title || "Diagnostic assessment"}
                </span>
                <span className="font-medium shrink-0">
                  £{(diagJG.total_inc_vat || diagJG.subtotal || 0).toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex items-baseline justify-between text-slate-800">
              <span className="flex-1 min-w-0 pr-3 truncate">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 block">Rectification</span>
                {rectJG.title}
              </span>
              <span className="font-semibold shrink-0">
                £{(rectJG.total_inc_vat || rectJG.subtotal || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex items-baseline justify-between border-t border-slate-200 pt-2 mt-2">
              <span className="font-semibold text-slate-900">Group total</span>
              <span className="font-bold text-slate-900">£{groupTotal.toFixed(2)}</span>
            </div>
          </div>

          {/* Customer narrative from the rectification (or fallback to diag) */}
          {(rectJG.customer_notes?.[0]?.text || diagJG?.customer_notes?.[0]?.text) && (
            <p className="text-sm text-slate-700 leading-relaxed pt-1">
              {rectJG.customer_notes?.[0]?.text || diagJG?.customer_notes?.[0]?.text}
            </p>
          )}

          {/* Variant chooser */}
          {rectVariants.length > 0 && (
            <div className="pt-2 space-y-1.5">
              <p className="text-xs font-semibold text-slate-700">Choose your option:</p>
              <div className="grid grid-cols-1 gap-1">
                {rectVariants.map((v) => (
                  <button
                    key={v.code}
                    onClick={() => handleSwitchVariant(v.code)}
                    disabled={swapping || v.code === activeVariant}
                    className={`text-left text-xs py-2 px-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                      v.code === activeVariant
                        ? "bg-emerald-500 text-white border-emerald-600 cursor-default"
                        : "bg-white border-slate-200 hover:border-slate-400 disabled:opacity-50"
                    }`}
                    data-testid={`rect-variant-${v.code}-${rectJG.id}`}
                  >
                    <span>{v.label}</span>
                    {v.code === activeVariant && !swapping && <Check className="w-4 h-4" />}
                    {v.code === activeVariant && swapping && <Loader2 className="w-4 h-4 animate-spin" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Warranty chip */}
          {rectJG.warranty && (
            <p className="text-xs text-slate-500 italic">
              ✓ {rectJG.warranty}
            </p>
          )}

          {/* Approve / Skip action */}
          <div className="grid grid-cols-2 gap-2 pt-2">
            <button
              onClick={() => onApprove && onApprove(rectJG.id)}
              disabled={isApproved}
              className={`py-2.5 px-3 rounded-lg font-medium text-sm transition-all ${
                isApproved
                  ? "bg-emerald-500 text-white cursor-default"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white"
              }`}
              data-testid={`rect-approve-${rectJG.id}`}
            >
              {isApproved ? "✓ Approved" : "Approve group"}
            </button>
            <button
              onClick={() => onSkip && onSkip(rectJG.id)}
              className="py-2.5 px-3 rounded-lg font-medium text-sm border-2 border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:border-slate-300 transition-all"
              data-testid={`rect-skip-${rectJG.id}`}
            >
              Skip for now
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
